import React, { useState, useEffect } from "react";
import { Table } from "antd";
import { useSelector } from "react-redux";
import { useGetEmployeeHikeDetailsQuery } from "AppQuries/employee/get-employee-details";
import { loggedInUser } from "core/redux/slice/authSlice";
import { getCurrentFormattedDate } from "core/Operations/dateFormat";
import TableTop from "Components/UI/TableTop";
import { useTableSearch } from "core/Operations/useTableSearch";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, PlusCircle } from "react-feather";
import { all_routes } from "Router/all_routes";
import HikeModal from "Components/Modal/hike";
import { add, format } from "date-fns";
import { FormProvider, useForm } from "react-hook-form";
import Error404 from "MainPages/ErrorPages/error404";
import { yupResolver } from "@hookform/resolvers/yup";
import { hikeDetailValidation } from "core/Operations/ValidationSchema/hikeDetailValidation";
import {
  useInsertHikeDetailMutation,
  useUpdateHikeDetailMutation,
} from "AppQuries/employee/hike-details";
import { toast } from "core/Operations/toast";
import ApproveModal from "Components/Modal/approve";

const HikeDetails = () => {
  const userData = useSelector(loggedInUser);
  const [searchParams] = useSearchParams();
  const [employeeCode, setEmployeeCode] = useState(searchParams.get("employeeCode") ?? "");
  const [companyCode, setCompanyCode] = useState(searchParams.get("companyCode") ?? "");

  const [showModal, setShowModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);

  const initialValues = {
    code: 0,
    companyCode: companyCode ?? 0,
    locationCode: userData?.locationCode ?? "",
    bonus: 0,
    other_Allowances_Compensation: 0,
    approvalSignature: "",
    employeeSignature: "",
    effectiveDate: "",
    approvedBy: "",
    approvedDate: "",
    experienceYears: 0,
    isApproved: false,
    salaryType: "",
    createUser: userData?.userName ?? "",
    createDate: getCurrentFormattedDate(),
    modifyUser: userData?.userName ?? "",
    modifyDate: getCurrentFormattedDate(),
    isActive: true,
  };

  const {
    salaryHikeDetails,
    employeeInfo,
    isLoading,
    isError,
    error,
    refetch: getHikeDetails,
  } = useGetEmployeeHikeDetailsQuery(
    {
      companyCode,
      employeeCode,
      locationCode: userData.locationCode,
    },
    {
      enabled: !!companyCode && !!employeeCode,
    }
  );

  const { mutate: addHikeDetail } = useInsertHikeDetailMutation();
  const { mutate: updateHikeDetail } = useUpdateHikeDetailMutation();

  const methods = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(hikeDetailValidation),
  });

  const { reset } = methods;

  const columns = [
    {
      title: "Position",
      dataIndex: "position_Title",
    },
    {
      title: "Current Base Salary",
      dataIndex: "currentBaseSalary",
    },
    {
      title: "Hike Percentage",
      dataIndex: "hikePercentage",
    },
    {
      title: "Hike Amount",
      dataIndex: "hikeAmount",
    },
    {
      title: "New Salary",
      dataIndex: "newSalary",
    },
    {
      title: "Approved By",
      dataIndex: "approvedBy",
      render: (_, record) =>
        record.isApproved ? (
          "Approved"
        ) : (
          <button
            className="btn btn-primary"
            onClick={() => onApproveHandler(record, true)}
          >
            Need to Approve
          </button>
        ),
    },
    {
      title: "Approved Date",
      dataIndex: "approvedDate",
      render: (text) => (text ? format(new Date(text), "dd-MM-yyyy") : "-"),
    },
    {
      title: "Effective Date",
      dataIndex: "effectiveDate",
      render: (text) => (text ? format(new Date(text), "dd-MM-yyyy") : "-"),
    },
  ];

  const onSubmit = (data) => {
    if (selectedData) {
      updateHikeDetailHandler(data);
    } else {
      addHikeDetail([data], {
        onSuccess: (res) => {
          toast.success("Hike added successfully.");
          getHikeDetails();
          setShowModal(false);
          setSelectedData(null);
          reset(initialValues);
          setAddNewHike(false);
        },
        onError: (err) => {
          toast.error(err.message || "Something went wrong.");
        },
      });
    }
  };

  const onApproveHandler = (data, status) => {
    reset({
      ...initialValues,
      ...data,
      modifyUser: userData.userName,
      modifyDate: getCurrentFormattedDate(),
    });
    setShowModal(status);
    setSelectedData(data);
  };

  const updateHikeDetailHandler = (data) => {
    const { salaryType, ...rest } = data;
    updateHikeDetail(rest, {
      onSuccess: () => {
        toast.success("Hike updated successfully.");
        getHikeDetails();
        setShowModal(false);
        setSelectedData(null);
        reset(initialValues);
        setAddNewHike(false);
      },
      onError: (err) => {
        toast.error(err.message || "Something went wrong.");
      },
    });
  };

  const searchColumns = columns.map((column) => column.dataIndex);
  const { setSearchValue, filteredData } = useTableSearch(
    salaryHikeDetails,
    searchColumns
  );

  const [addNewHike, setAddNewHike] = useState(false);
  const addNewHikeHandler = () => {
    
    setAddNewHike(true);
  };

  useEffect(() => {
    if (addNewHike && employeeInfo?.length) {
      const emp = employeeInfo[0];
      reset({
        ...initialValues,
        companyCode: emp.companyCode ?? 0,
        employeeCode: emp.employeeCode ?? 0,
        department: emp.departmentCode ?? 0,
        position_Title: emp.jobTitle ?? "",
        salaryType : emp.salaryType ?? "",
        currentBaseSalary:
          emp.salaryType === "Monthly"
            ? emp.basicSalary ?? 0
            : emp.salaryType === "Hourly"
            ? emp.hourlySalary ?? 0
            : emp.salaryType === "Daily"
            ? emp.daySalary ?? 0
            : 0,
      });
      setShowModal(true);
    }
  }, [addNewHike, employeeInfo, reset, userData]);

  useEffect(() => {
    if (employeeInfo?.length) {
      const emp = employeeInfo[0];
      reset({
        ...initialValues,
        companyCode: emp.companyCode ?? 0,
        employeeCode: emp.employeeCode ?? 0,
        department: emp.departmentCode ?? 0,
        position_Title: emp.jobTitle ?? "",
        currentBaseSalary:
          emp.salaryType === "Monthly"
            ? emp.basicSalary ?? 0
            : emp.salaryType === "Hourly"
            ? emp.hourlySalary ?? 0
            : emp.salaryType === "Daily"
            ? emp.daySalary ?? 0
            : 0,
      });
    }
  }, [employeeInfo, reset, userData]);

  if (isError) return <Error404 isError={isError} error={error} />;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Employee Salary Hike Details</h4>
              <h6>View and manage salary hike</h6>
            </div>
          </div>
          <div className="page-btn">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="add-new-tooltip">Add New Hike</Tooltip>}
            >
              <button
                type="button"
                className="btn btn-primary"
                onClick={addNewHikeHandler}
              >
                <PlusCircle className="me-2" />
                New Hike
              </button>
            </OverlayTrigger>
          </div>
          <div className="page-btn">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="add-new-tooltip">Back</Tooltip>}
            >
              <Link
                to={`${all_routes.employee}${all_routes.employee_view}/${employeeCode}`}
                className="btn btn-secondary"
              >
                <ArrowLeft className="me-2" />
                Back
              </Link>
            </OverlayTrigger>
          </div>
        </div>
        <div className="card border-0">
          <div className="card-body">
            <div className="row">
              <div className="col-md-12">
                <div className="table-list-card">
                  <TableTop
                    onSearch={setSearchValue}
                    toggleFilterVisibility={() => {}}
                    showToggle={false}
                    isFilterVisible={false}
                  />
                  <div className="table-responsive">
                    <Table
                      loading={isLoading}
                      columns={columns}
                      dataSource={filteredData}
                      rowKey="code"
                      pagination={true}
                      bordered
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FormProvider {...methods}>
        <HikeModal
          onSubmit={onSubmit}
          show={showModal}
          handleClose={() => {
            setAddNewHike(false);
            setShowModal(false);
          }}
          locationCode={userData?.locationCode}
          companyCode={userData?.companyCode}
        />
      </FormProvider>
      <FormProvider {...methods}>
        <ApproveModal
          show={showApproveModal}
          locationCode={userData?.locationCode}
          companyCode={userData?.companyCode}
          handleClose={() => {
            setShowApproveModal(false);
            reset(initialValues);
          }}
          onSubmit={onSubmit}
        />
      </FormProvider>
    </div>
  );
};

export default React.memo(HikeDetails);
